import { Button } from "@gigpro/ui/Button";
import { Dialog, DialogFooter, DialogTitle } from "@gigpro/ui/Dialog";
import { Form } from "@gigpro/ui/Form";
import { Checkbox } from "@gigpro/ui/Form/Checkbox";
import { useZodForm } from "@gigpro/ui/Form/utils";
import { env } from "config/env";
import { useAuth } from "domains/auth";
import { useUpdateToSAcceptance } from "domains/signUp/api/tos";
import type React from "react";
import { type Control, type FieldPath, type FieldValues, useFormState } from "react-hook-form";
import { toastApiError } from "utils/toastApiError";
import * as z from "zod/mini";

export const tosSchema = z._default(z.boolean(), false).check(
  z.superRefine((val, ctx) => {
    if (val) return;
    ctx.addIssue({
      code: "custom",
      message: "You must accept the terms of service to continue",
    });
  }),
);

// Radix swallows Enter on a checkbox. Restore native implicit submission, which goes through the submit button so its
// disabled/loading state still blocks a second request.
function submitOnEnter(event: React.KeyboardEvent<HTMLButtonElement>) {
  if (event.key !== "Enter" || event.repeat) return;
  const submitter = event.currentTarget.form?.querySelector<HTMLButtonElement>('button[type="submit"]');
  if (submitter && !submitter.disabled) submitter.click();
}

export function TosCheckbox<TFieldValues extends FieldValues>({
  control,
  name,
}: Readonly<{ control: Control<TFieldValues>; name: FieldPath<TFieldValues> }>) {
  return (
    <Checkbox
      name={name}
      control={control}
      label={<TosCopy />}
      className="[&>div]:items-start [&>div>button]:mt-0.5"
      onKeyDown={submitOnEnter}
    />
  );
}

export function TosCopy() {
  return (
    <p className="block text-wrap text-black sm:text-large [&>a]:font-semibold [&>a]:text-brand [&>a]:hover:underline">
      By checking this box, you agree to receive SMS messages from Qwick at the number provided about your account and
      shift postings, and you agree to{" "}
      <a href={env.REACT_APP_MARKETPLACE_PLATFORM_TERMS_URL} target="_blank">
        Qwick's Marketplace Platform Terms
      </a>
      ,{" "}
      <a href={env.REACT_APP_BUSINESS_SERVICES_AGREEMENT_URL} target="_blank">
        Business Services Agreement
      </a>
      , and{" "}
      <a href={env.REACT_APP_PRIVACY_POLICY_URL} target="_blank">
        Privacy Policy
      </a>
      {""}. Message frequency varies. Message and data rates may apply. Reply HELP for help or STOP to cancel.
    </p>
  );
}

const tosCopyOptions = {
  existingUser: {
    title: "We’ve updated our Terms of Service",
    bodyPrefix: "By using this platform",
    button: "Confirm and exit",
  },
  newUser: {
    title: "Please review and accept our terms of service to continue",
    bodyPrefix: "By creating an account",
    button: "Accept and continue",
  },
};

const formSchema = z.object({
  tosAccepted: z.literal(true, {
    // don't want to show any error message
    error: "",
  }),
});

export function ToSDialog() {
  const { logout, signUpRequired, tosAccepted, linkableAccountsStatus } = useAuth();

  const tosCopy = signUpRequired ? tosCopyOptions.newUser : tosCopyOptions.existingUser;

  const updateToSAcceptance = useUpdateToSAcceptance();

  const form = useZodForm({ schema: formSchema });
  const { isSubmitting, isValid } = useFormState({ control: form.control });
  const onSubmit = async () => {
    try {
      await updateToSAcceptance.mutateAsync({ id: "me" });
    } catch (error) {
      toastApiError(error);
    }
  };
  return (
    // Linking resolves first: a manager who links may land in an older account that already accepted.
    <Dialog open={!tosAccepted && linkableAccountsStatus !== "linking-required"}>
      <DialogTitle hideCloseButton>{tosCopy.title}</DialogTitle>
      <Form form={form} onSubmit={onSubmit}>
        <label htmlFor="tosAccepted" className="mb-2 flex gap-1 text-large text-wrap">
          <Checkbox id="tosAccepted" control={form.control} name="tosAccepted" className="mt-0.5" />
          <span className="[&>a]:font-semibold [&>a]:text-brand [&>a]:hover:underline">
            {tosCopy.bodyPrefix} you agree to{" "}
            <a href={env.REACT_APP_MARKETPLACE_PLATFORM_TERMS_URL} target="_blank">
              Qwick’s Marketplace Platform Terms
            </a>
            ,{" "}
            <a href={env.REACT_APP_BUSINESS_SERVICES_AGREEMENT_URL} target="_blank">
              Business Services Agreement
            </a>{" "}
            and{" "}
            <a href={env.REACT_APP_PRIVACY_POLICY_URL} target="_blank">
              Privacy Policy
            </a>
            {""}. Please review these documents before proceeding.
          </span>
        </label>
        <DialogFooter>
          <Button onClick={logout}>Logout</Button>
          <Button variant="contained" type="submit" loading={isSubmitting} disabled={!isValid}>
            {tosCopy.button}
          </Button>
        </DialogFooter>
      </Form>
    </Dialog>
  );
}
